import { Grid, Box, Divider, styled } from "@mui/material";
import { applyTimestampHeuristic } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import AcxChip from "components/UI/AcxChip";
import { AcxExpandableText } from "components/UI/AcxExpandableText";
import { Duration } from "components/UI/Duration";
import { TestLLMConversation } from "models/LLMs/Testing/TestLLMConversation";
import moment from "moment";
import React from "react";
import { ResultBadge } from "./ResultBadge";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { WouldNotExecuteUnderConditionsWarning } from "./WouldNotExecuteUnderConditionsWarning";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import {
    ExtractedMetadataList,
    ExtractedMetadataItem,
} from "components/UI/ExtractedMetadataList";

const LabelText = styled(Box)(({ theme }) => ({
    color: theme.palette.gray[500],
    fontWeight: 700,
    fontSize: "12px",
}));

const LightGrayTextGrid = styled(Grid)(({ theme }) => ({
    color: theme.palette.gray[500],
    lineHeight: "20px",
    fontSize: "14px",
}));

interface Labels {
    human?: string | null;
    model?: string | null;
}

interface WrittenMetadata {
    classifier?: ExtendedMetadataClassifier;
    value: unknown;
}

interface TestedConversationDetailsProps {
    conversation: TestLLMConversation;
    labels?: Labels;
    number?: number;
    wouldExecuteUnderConditions?: boolean;
    writtenMetadata?: WrittenMetadata[];
    /** Where a labeled conversation came from. Shown as a chip when set. */
    source?: "human" | "synthetic";
}

export function TestedConversationDetails({
    conversation,
    number,
    labels,
    wouldExecuteUnderConditions,
    writtenMetadata,
    source,
}: TestedConversationDetailsProps) {
    const store = useStore(LLMClassifierBuilderStore);

    return (
        <Grid container direction="column" flexWrap="nowrap" rowGap={1}>
            <Grid container item justifyContent="space-between">
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={1 / 2}
                    width="fit-content"
                >
                    {number !== undefined && (
                        <Grid item>
                            <Box
                                component="span"
                                sx={{
                                    color: "#2C3F92",
                                    fontWeight: 700,
                                    pr: 1,
                                }}
                            >
                                #{number + 1}
                            </Box>
                        </Grid>
                    )}
                    {source && (
                        <Grid item paddingRight={1}>
                            <AcxChip
                                size="small"
                                color={
                                    source === "synthetic"
                                        ? "lightBlue"
                                        : "gray"
                                }
                                label={
                                    source === "synthetic"
                                        ? "Synthetic"
                                        : "Human label"
                                }
                            />
                        </Grid>
                    )}
                    {/* Synthetic calls have no recording, so no duration. */}
                    {source !== "synthetic" && (
                        <LightGrayTextGrid item>
                            {/* The DTO types this as a non-nullable long, so an
                                unknown duration reaches us as 0. Collapse that back
                                to undefined so it stays blank rather than 00:00. */}
                            <Duration
                                ms={
                                    conversation.callDurationMillis || undefined
                                }
                            />
                        </LightGrayTextGrid>
                    )}
                    {conversation.timestamp && (
                        <React.Fragment>
                            {source !== "synthetic" && (
                                <Divider orientation="vertical" />
                            )}
                            <LightGrayTextGrid item>
                                {source === "synthetic" && "Generated "}
                                {moment(
                                    applyTimestampHeuristic(
                                        conversation.timestamp,
                                    ),
                                ).format("MM/DD/YYYY")}
                            </LightGrayTextGrid>
                        </React.Fragment>
                    )}
                    {wouldExecuteUnderConditions === false && (
                        <WouldNotExecuteUnderConditionsWarning />
                    )}
                </Grid>
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={2}
                    width="fit-content"
                >
                    {labels?.model && (
                        <Grid
                            container
                            item
                            alignItems="center"
                            width="fit-content"
                            columnGap={1 / 2}
                        >
                            <LabelText>Model:</LabelText>
                            <ResultBadge
                                color={store.getResultColor(labels.model)}
                            >
                                {labels.model}
                            </ResultBadge>
                        </Grid>
                    )}
                    {labels?.human && (
                        <Grid
                            container
                            item
                            alignItems="center"
                            width="fit-content"
                            columnGap={1 / 2}
                        >
                            <LabelText>Labeled:</LabelText>
                            <ResultBadge
                                color={store.getResultColor(labels.human)}
                            >
                                {labels.human}
                            </ResultBadge>
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item>
                <AcxExpandableText>
                    <Box fontWeight="bold" component="span">
                        Summary:
                    </Box>{" "}
                    <span>{conversation.summary ?? "N/A"}</span>
                </AcxExpandableText>
            </Grid>
            {writtenMetadata && writtenMetadata.length > 0 && (
                <Grid item>
                    <Box fontWeight="bold" component="span">
                        Extracted Metadata:
                    </Box>
                    <ExtractedMetadataList
                        items={writtenMetadata.reduce<ExtractedMetadataItem[]>(
                            (acc, { classifier, value }, index) => {
                                if (!classifier) return acc;
                                acc.push({
                                    key: index,
                                    name: classifier.name,
                                    value,
                                    jsonSchema: classifier.jsonSchema,
                                });
                                return acc;
                            },
                            [],
                        )}
                    />
                </Grid>
            )}
        </Grid>
    );
}
